import EventEmitter from 'wolfy87-eventemitter';
import queryString from 'query-string';

const createLoader = function (loader) {
  let getLibrary = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let loading = false;
  let loaded = false;
  let loadedLibrary = null;
  const events = new EventEmitter();
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return new Promise(resolve => {
      if (loadedLibrary === null && getLibrary !== null) {
        loadedLibrary = getLibrary(...args);
      }
      if (loaded || loadedLibrary !== null) {
        resolve(loadedLibrary);
        return;
      }
      if (loading) {
        events.once('loaded', () => resolve(loadedLibrary));
        return;
      }
      loading = true;
      loader(...args).then(function () {
        let newLibrary = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
        loadedLibrary = newLibrary;
        if (loadedLibrary === null && getLibrary !== null) {
          loadedLibrary = getLibrary(...args);
        }
        loaded = true;
        resolve(loadedLibrary);
        events.emit('loaded', loadedLibrary);
      });
    });
  };
};

const loadScript = url => new Promise(resolve => {
  const script = document.createElement('script');
  script.src = url;
  script.onload = () => resolve();
  document.getElementsByTagName('head')[0].appendChild(script);
});

const loadScriptWithCallback = (url, callback) => new Promise(resolve => {
  window[callback] = () => resolve();
  const script = document.createElement('script');
  script.src = url;
  document.getElementsByTagName('head')[0].appendChild(script);
});

const loadDailymotion = createLoader(function () {
  let {
    url = 'https://api.dmcdn.net/all.js',
    callback = 'dmAsyncInit'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return callback !== null ? loadScriptWithCallback(url, callback) : loadScript(url);
}, () => typeof window !== 'undefined' ? window.DM || window.dailymotion || null : null);

const loadFacebookScript = function (d, s, id, locale) {
  let debug = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  const fjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) {
    return;
  }
  const js = d.createElement(s);
  js.id = id;
  js.src = `//connect.facebook.net/${locale}/sdk${debug ? '/debug' : ''}.js`;
  fjs.parentNode.insertBefore(js, fjs);
};
const loadFacebook = createLoader(opts => new Promise(resolve => {
  const options = {
    appId: null,
    locale: 'EN_US',
    autoLogAppEvents: true,
    cookie: true,
    state: true,
    version: 'v12.0',
    debug: false,
    ...opts
  };
  window.fbAsyncInit = () => {
    window.FB.init({
      ...options
    });
    window.FB.AppEvents.logPageView();
    resolve(window.FB);
  };
  loadFacebookScript(document, 'script', 'facebook-jssdk', options.locale, options.debug);
}), () => typeof window.FB !== 'undefined' ? window.FB : null);

const loadGoogleApi = createLoader(function () {
  let {
    url = 'https://apis.google.com/js/api.js',
    callback = `onGoogleApiLoaded_${new Date().getTime()}`,
    withClient = true
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScriptWithCallback(`${url}?${queryString.stringify({
    onload: callback
  })}`, callback).then(() => withClient ? new Promise(resolve => {
    window.gapi.load('client', resolve);
  }) : null);
}, function () {
  let {
    withClient = true
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return typeof window.gapi !== 'undefined' && (!withClient || typeof window.gapi.client !== 'undefined') ? window.gapi : null;
});

const loadGoogleGsi = createLoader(function () {
  let {
    url = 'https://accounts.google.com/gsi/client'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url);
}, () => typeof window.google !== 'undefined' && typeof window.google.accounts !== 'undefined' ? window.google.accounts : null);

const loadGoogleIma = createLoader(function () {
  let {
    url = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url);
}, () => typeof window.google !== 'undefined' && typeof window.google.ima !== 'undefined' ? window.google.ima : null);

const loadGoogleMaps = createLoader(function () {
  let {
    url = 'https://maps.googleapis.com/maps/api/js',
    locale = 'en',
    key = null,
    apiKey = null,
    libraries = null,
    callback = `onGoogleMapsLoaded_${new Date().getTime()}`
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScriptWithCallback(`${url}?${queryString.stringify({
    key: apiKey || key,
    callback,
    libraries: libraries !== null ? libraries.join(',') : null,
    language: locale
  })}`, callback);
}, () => typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined' ? window.google : null);

const loadInstagram = createLoader(function () {
  let {
    url = 'https://www.instagram.com/embed.js'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url);
}, () => typeof window.instgrm !== 'undefined' ? window.instgrm : null);

const loadPlayer = createLoader(function () {
  let {
    clientId = 'radiocanadaca_tele',
    url = '//services.radio-canada.ca/media/player/client/{clientId}'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url.replace(/\{\s*clientId\s*\}/gi, clientId));
}, () => typeof window.RadioCanada !== 'undefined' && typeof window.RadioCanada.player !== 'undefined' ? window.RadioCanada.player : null);

const loadStripeCheckout = createLoader(function () {
  let {
    url = 'https://checkout.stripe.com/checkout.js'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url);
}, () => typeof window.StripeCheckout !== 'undefined' ? window.StripeCheckout : null);

const loadTikTok = createLoader(function () {
  let {
    url = 'https://www.tiktok.com/embed.js'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScript(url);
}, () => typeof window.tiktok !== 'undefined' ? window.tiktok : null);

const loadTwitter = createLoader(function () {
  let {
    url = 'https://platform.twitter.com/widgets.js'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return new Promise(resolve => {
    const twttr = ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      const t = window.twttr || {};
      if (d.getElementById(id)) {
        resolve(t);
        return t;
      }
      const js = d.createElement(s);
      js.id = id;
      js.src = url;
      fjs.parentNode.insertBefore(js, fjs);

      /* eslint-disable no-underscore-dangle */
      t._e = [];
      t.ready = f => {
        t._e.push(f);
      };
      /* eslint-enable no-underscore-dangle */
      return t;
    })(document, 'script', 'twitter-wjs');
    twttr.ready(resolve);
    window.twttr = twttr;
  });
}, () => typeof window.twttr !== 'undefined' && typeof window.twttr.widgets !== 'undefined' ? window.twttr : null);

const loadVimeo = createLoader(() => import('@vimeo/player').then(_ref => {
  let {
    default: Player
  } = _ref;
  return Player;
}), () => typeof window.Vimeo !== 'undefined' && typeof window.Vimeo.Player !== 'undefined' ? window.Vimeo.Player : null);

const loadYouTube = createLoader(function () {
  let {
    url = 'https://www.youtube.com/iframe_api',
    callbackName = 'onYouTubeIframeAPIReady'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return loadScriptWithCallback(url, callbackName);
}, () => typeof window.YT !== 'undefined' ? window.YT : null);

export { createLoader, loadDailymotion, loadFacebook, loadGoogleApi, loadGoogleGsi, loadGoogleIma, loadGoogleMaps, loadInstagram, loadScript, loadScriptWithCallback, loadPlayer as loadSrcPlayer, loadStripeCheckout, loadTikTok, loadTwitter, loadVimeo, loadYouTube };
